/**
 * Store de histórico para Undo/Redo
 * 
 * @module stores/undoRedoStore
 * @description Implementa sistema de desfazer/refazer com stack de 20 ações
 * e keyboard shortcuts (Ctrl+Z / Ctrl+Shift+Z)
 */

import { create } from 'zustand';
import { DREData } from '../types/dre';

interface HistoryAction {
  id: string;
  timestamp: Date;
  dreData: DREData;
  description: string;
}

interface UndoRedoStore {
  undoStack: HistoryAction[];
  redoStack: HistoryAction[];
  maxStackSize: number;
  
  // Actions
  pushAction: (dreData: DREData, description: string) => void;
  undo: () => DREData | null;
  redo: () => DREData | null;
  canUndo: () => boolean;
  canRedo: () => boolean;
  clear: () => void;
  getUndoDescription: () => string | null;
  getRedoDescription: () => string | null;
}

export const useUndoRedoStore = create<UndoRedoStore>((set, get) => ({
  undoStack: [],
  redoStack: [],
  maxStackSize: 20,

  pushAction: (dreData, description) => {
    set((state) => {
      const action: HistoryAction = {
        id: `action_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        timestamp: new Date(),
        dreData: JSON.parse(JSON.stringify(dreData)), // Deep clone
        description,
      };

      const newUndoStack = [...state.undoStack, action];

      // Limitar stack a maxStackSize
      if (newUndoStack.length > state.maxStackSize) {
        newUndoStack.shift(); // Remove o mais antigo
      }

      return {
        undoStack: newUndoStack,
        redoStack: [], // Limpa redo ao fazer nova ação
      };
    });
  },

  undo: () => {
    const state = get();
    if (state.undoStack.length === 0) return null;

    const actionToUndo = state.undoStack[state.undoStack.length - 1];
    
    set({
      undoStack: state.undoStack.slice(0, -1),
      redoStack: [...state.redoStack, actionToUndo],
    });

    return actionToUndo.dreData;
  },

  redo: () => {
    const state = get();
    if (state.redoStack.length === 0) return null;

    const actionToRedo = state.redoStack[state.redoStack.length - 1];
    
    set({
      undoStack: [...state.undoStack, actionToRedo],
      redoStack: state.redoStack.slice(0, -1),
    });

    return actionToRedo.dreData;
  },

  canUndo: () => get().undoStack.length > 0,

  canRedo: () => get().redoStack.length > 0,

  clear: () => set({ undoStack: [], redoStack: [] }),

  getUndoDescription: () => {
    const stack = get().undoStack;
    return stack.length > 0 ? stack[stack.length - 1].description : null;
  },

  getRedoDescription: () => {
    const stack = get().redoStack;
    return stack.length > 0 ? stack[stack.length - 1].description : null;
  },
}));
